import { auteurs, conjointsDe, enfantsDe, generationDe, parentsDe } from './modele.js';
import type { DonneesArbre } from './types.js';

export interface Deplacement {
  focusId: string;
  ouvrir: string | null;
  ignoree?: boolean;
}

/** Ordre de lecture : le couple d'auteurs, puis chaque branche en profondeur, le conjoint juste après son partenaire. */
export function ordreDeLecture(d: DonneesArbre): string[] {
  const racines = auteurs(d);
  const ordre: string[] = [];
  const visite = (id: string): void => {
    if (ordre.includes(id)) return;
    ordre.push(id);
    for (const c of conjointsDe(d, id)) if (!racines.includes(c) && !ordre.includes(c)) ordre.push(c);
    for (const e of enfantsDe(d, id)) visite(e);
  };
  for (const r of racines) if (!ordre.includes(r)) ordre.push(r);
  for (const r of racines) for (const e of enfantsDe(d, r)) visite(e);
  for (const p of d.personnes) if (!ordre.includes(p.id)) ordre.push(p.id);
  return ordre;
}

/**
 * Déplacement du focus selon la touche pressée ; les touches inconnues sont signalées, jamais avalées. `visibles` : les
 * nœuds réellement affichés par la disposition courante, le focus ne se pose jamais ailleurs.
 */
export function deplacer(d: DonneesArbre, focusId: string, touche: string, visibles?: readonly string[]): Deplacement {
  const rendu = visibles ? new Set(visibles) : null;
  const estRendu = (id: string): boolean => rendu === null || rendu.has(id);
  const ordre = ordreDeLecture(d).filter(estRendu);
  const premier = ordre[0] ?? focusId;
  if (!ordre.includes(focusId)) return { focusId: premier, ouvrir: null };
  const voisins = ordre.filter((id) => generationDe(d, id) === generationDe(d, focusId));
  const rang = voisins.indexOf(focusId);
  switch (touche) {
    case 'ArrowDown':
      return { focusId: enfantsDe(d, focusId).find(estRendu) ?? focusId, ouvrir: null };
    case 'ArrowUp':
      return { focusId: parentsDe(d, focusId).find(estRendu) ?? focusId, ouvrir: null };
    case 'ArrowRight':
      return { focusId: voisins[Math.min(rang + 1, voisins.length - 1)] ?? focusId, ouvrir: null };
    case 'ArrowLeft':
      return { focusId: voisins[Math.max(rang - 1, 0)] ?? focusId, ouvrir: null };
    case 'Home':
      return { focusId: premier, ouvrir: null };
    case 'End':
      return { focusId: ordre[ordre.length - 1] ?? focusId, ouvrir: null };
    case 'Enter':
    case ' ':
      return { focusId, ouvrir: focusId };
    default:
      return { focusId, ouvrir: null, ignoree: true };
  }
}
